import { type ForwardedRef, useCallback, useImperativeHandle, useMemo } from 'react'

import type { ChatAttachment } from './chat-input-attachment.interface'
import { canSendMessage } from './chat-input-composer.helper'
import type { ChatInputComposerRef } from './chat-input-composer.interface'
import type { ChatInputComposerBodyProps } from './chat-input-composer-body.atom'
import { buildBodyProps } from './chat-input-composer-body.helper'
import type { ChatInputMode } from './chat-input-mode.type'
import type { PromptTemplate } from './prompt-template.interface'
import type { UseChatInputFileReturn } from './use-chat-input-file.hook'
import { useChatInputFile } from './use-chat-input-file.hook'
import type { UseChatInputTextareaReturn } from './use-chat-input-textarea.hook'
import { useChatInputTextarea } from './use-chat-input-textarea.hook'

interface BodyPropsInput {
  params: UseChatInputComposerParams
  mode: ChatInputMode
  canSend: boolean
  textarea: UseChatInputTextareaReturn
  file: UseChatInputFileReturn
  handleModeChange: (m: ChatInputMode) => void
  handleSend: () => void
  isStreaming?: boolean
  isStopping?: boolean
  onStop?: () => void
}

const createBodyProps = (input: BodyPropsInput): ChatInputComposerBodyProps =>
  buildBodyProps({
    value: input.params.value,
    mode: input.mode,
    placeholder: input.params.placeholder,
    maxHeight: input.params.maxHeight,
    isDisabled: input.params.isDisabled,
    isFileAttachEnabled: input.params.isFileAttachEnabled,
    isLoading: input.params.isLoading,
    isUploading: input.params.isUploading,
    uploadProgress: input.params.uploadProgress,
    isNoteMode: input.mode === 'note',
    canSend: input.canSend,
    attachmentList: input.params.attachmentList,
    textarea: input.textarea,
    file: input.file,
    handleModeChange: input.handleModeChange,
    handleSend: input.handleSend,
    isStreaming: input.isStreaming,
    isStopping: input.isStopping,
    onStop: input.onStop,
    templateList: input.params.templateList,
    handleTemplateSelect: input.params.onTemplateSelect,
    handleCreateTemplate: input.params.onCreateTemplate,
  })

interface UseChatInputComposerParams {
  value: string
  onChange: (value: string) => void
  mode: ChatInputMode
  onModeChange?: (mode: ChatInputMode) => void
  attachmentList: ChatAttachment[]
  onFilesSelected?: (fileList: File[]) => void
  onAttachmentRemove?: (id: string) => void
  onAttachFromChats?: () => void
  onSend: () => void
  placeholder?: string
  maxHeight: number
  isDisabled: boolean
  isFileAttachEnabled: boolean
  isLoading: boolean
  isStreaming?: boolean
  isStopping?: boolean
  onStop?: () => void
  isUploading: boolean
  uploadProgress: number
  ref: ForwardedRef<ChatInputComposerRef>
  templateList?: PromptTemplate[]
  onTemplateSelect?: (templateId: string) => void
  onCreateTemplate?: () => void
}

export const useChatInputComposer = (params: UseChatInputComposerParams) => {
  const { mode, onModeChange, onSend, ref } = params
  const canSend = canSendMessage(
    params.value,
    params.attachmentList.length,
    params.isDisabled,
    params.isLoading
  )
  const textarea = useChatInputTextarea({
    value: params.value,
    maxHeight: params.maxHeight,
    isDisabled: params.isDisabled,
    isLoading: params.isLoading,
    attachmentCount: params.attachmentList.length,
    onChange: params.onChange,
    onSend,
    onFilesSelected: params.onFilesSelected,
  })
  const file = useChatInputFile({
    onFilesSelected: params.onFilesSelected,
    onAttachFromChats: params.onAttachFromChats,
    onAttachmentRemove: params.onAttachmentRemove,
  })
  useImperativeHandle(ref, () => ({ focus: textarea.focus, openFilePicker: file.openFilePicker }))
  const handleModeChange = useCallback((m: ChatInputMode) => onModeChange?.(m), [onModeChange])
  const handleSend = useCallback(() => canSend && onSend(), [canSend, onSend])
  const bodyProps = useMemo(
    () =>
      createBodyProps({
        params,
        mode,
        canSend,
        textarea,
        file,
        handleModeChange,
        handleSend,
        isStreaming: params.isStreaming,
        isStopping: params.isStopping,
        onStop: params.onStop,
      }),
    [params, mode, canSend, textarea, file, handleModeChange, handleSend]
  )
  return { isNoteMode: mode === 'note', canSend, bodyProps }
}
